<template>
  <div class="tab-page-container">
    <terminal-status-modal />

    <div class="tab-payments-header">
      <!-- Search Header -->
      <table>
        <b-row align-h="center" v-if="!selectedTab && !isLoading">
          <b-col md="8" class="my-1" style="text-align: left">
            <toggle-button
              class="active-toggle"
              color="#587294"
              style="font-size: 12pt"
              :value="openTabsOnly"
              :sync="true"
              :labels="{
                checked: $t('tab_toggle_open_label'),
                unchecked: $t('tab_toggle_open_label'),
              }"
              :width="125"
              :height="33"
              @change="openTabsOnly = !!$event.value"
            />

            <toggle-button
              class="active-toggle"
              color="#587294"
              style="font-size: 12pt"
              :value="activeTabsOnly"
              :sync="true"
              :labels="{
                checked: $t('tab_toggle_current_label'),
                unchecked: $t('tab_toggle_current_label'),
              }"
              :width="125"
              :height="33"
              @change="activeTabsOnly = !!$event.value"
            />
          </b-col>
          <b-col md="4" class="my-1">
            <b-input-group>
              <b-form-input
                v-model="filterString"
                :disabled="!!selectedTab"
                :placeholder="$t('search')"
              />
            </b-input-group>
          </b-col>
        </b-row>
      </table>
    </div>
    <!-- Tab Table -->
    <div class="tab-table">
      <b-table
        sort-by="createdAt"
        striped
        hover
        :sort-desc="true"
        :filter="filter"
        :filter-function="tableFilter"
        :fields="tabListFields"
        :items="tabsList"
        @row-clicked="loadTab"
      >
        <template slot="cell(guest_id)" slot-scope="data">
          <span class="tab-guest-name">
            {{ data.value }}
          </span>
        </template>
      </b-table>
    </div>

    <b-modal
      v-if="selectedTab"
      :visible="!!selectedTab"
      dialog-class="invoice-display-box"
      centered
      hide-header
      hide-footer
      hide-header-close
      @hidden="clearTabs"
    >
      <div slot="default" v-on:click.stop>
        <i @click="clearTabs" class="back-button fi fi-x"></i>
        <div class="guest-card">
          {{ selectedGuest }}
        </div>

        <guest-tab
          :class="{ 'locked-tab': !isOpen }"
          :tab="selectedTab"
          :removeCharge="removeItemFromTab"
        />

        <div v-if="isOpen" class="tab-actions">
          <div class="button-group">
            <!-- <b-button
              v-b-modal.enterItemModal
              class="invoice-control-button"
              variant="outline-primary"
              size="xs"
            >
              {{ $t('tab_modal_add_item_button') }}
            </b-button> -->

            <b-button
              v-b-modal.collectPaymentModal
              class="invoice-control-button"
              variant="outline-success"
              size="xs"
              @click="disableCardReader"
            >
              {{ $t("tab_modal_payments_button") }}
            </b-button>

            <!-- <b-button
              v-if="amountDue > 0"
              v-b-modal.collectPaymentModal
              class="invoice-control-button"
              size="xs"
              variant="success"
              @click="setBill"
            >
              {{ $t('tab_modal_settle_button') }} {{ formatMoney(amountDue) }}
            </b-button> -->

            <b-button
              v-if="amountDue === 0"
              class="invoice-control-button"
              size="xs"
              variant="outline-success"
              @click="closeOutTab"
            >
              Close Tab
            </b-button>
          </div>
        </div>

        <collect-payment-modal
          ref="paymentModal"
          :payer="selectedGuest"
          :tab="selectedTab"
          :loading="isRequestingPayment"
          @submit-payment="initiateCharge"
          @submit-adjustment="updateCurrentTab"
        />

        <tab-item-modal
          ref="itemModal"
          :items="availableItems"
          @submit="updateCurrentTab"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import { tabNameFormatter } from "@/utils/tab-name-formatter";
import { parseString } from "loader-utils";
import { formatMoney } from "@/utils/number-utils";

import { PAYMENT_TYPES, ITEM_TYPES, PAYMENT_METHODS } from "@/config/constants";

import {
  dispatchMakePaymentEvent,
  addPaymentCompletedEvent,
  removePaymentCompletedEvent,
} from "@/utils/events";

import TerminalMixin from "@/mixins/terminal-mixin";
import PaymentMixin from "@/mixins/payment-mixin";

export default {
  name: "Tabs",

  components: {
    TerminalStatusModal: () =>
      import("@/components/modals/TerminalStatusModal"),
    GuestTab: () => import("@/components/GuestTab"),
    CollectPaymentModal: () =>
      import("@/components/modals/CollectPaymentModal"),
    TabItemModal: () => import("@/components/modals/TabItemModal"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
  },

  mixins: [TerminalMixin, PaymentMixin],

  data() {
    return {
      isLoading: false,
      filterString: "",
      items: [],
      adjustments: [],
      selectedTab: null,
      selectedGuest: null,
      availableItems: [],
      tabListFields: [
        {
          key: "guest_id",
          label: this.$t("tab_guest_name"),
          sortable: true,
          tdClass: "tabs-label",
          formatter: (value) => {
            if (!value) {
              return "Walk in";
            } else {
              const guestInfo = this.getContacts.find(
                (contact) => contact.id === value
              );
              if (!guestInfo) return "Deleted Guest";
              return (
                `${guestInfo.first_name} ${guestInfo.last_name}` ||
                "Unknown Guest"
              );
            }
          },
        },
        {
          key: "createdAt",
          label: this.$t("tab_created"),
          sortable: true,
          formatter: (value) => {
            return this.$moment(value).format("MMMM Do YYYY, h:mm:ssa");
          },
        },
        {
          key: "subtotal",
          label: this.$t("tab_subtotal"),
          sortable: true,
        },
        {
          key: "balanceDue",
          label: this.$t("tab_balance_due"),
          sortable: true,
        },
        {
          key: "bookingId",
          sortable: true,
          label: this.$t("tab_booking"),
          formatter: (value) => {
            if (!value) {
              return "No Booking (Walk-in)";
            }

            const booking = this.getBookings.find(
              (booking) => booking.id === value
            );

            if (!booking) {
              return "Nonexistent Booking";
            }

            if (booking.reservations.length === 0) {
              return "No Rooms";
            }

            if (booking.reservations.length > 1) {
              return `${booking.reservations.length} Room Booking`;
            }

            if (!booking.reservations[0].unit) {
              return "Room no longer exists in system.";
            }

            //single reservation booking
            return booking.reservations[0].unit.unit_name;
          },
        },
      ],
      allTabs: [],
      openTabsOnly: true,
      activeTabsOnly: true,
      isOpen: null,
      isActive: null,
      tabsLoading: false,
    };
  },

  computed: {
    ...mapState({
      errored: (state) => state.tabs.errored,
      isRequestingTabs: (state) => state.tabs.isRequesting,
      stripeConnected: (state) => !!state.vendor.vendor.stripe_id,
    }),

    ...mapGetters([
      "getContacts",
      "getBookings",
      "getOpenTabs",
      "getAllWalkinTabs",
      "getAllTabs",
      "getPaymentTypes",
      "getAvailableItemsUnclassified",
      "getAvailableModifiers",
      "getActiveTabs",
      "getOpenActiveTabs",
      "getVendor",
    ]),

    tabsList() {
      return this.allTabs;
    },

    filter() {
      return " ";
    },

    amountDue() {
      return this.selectedTab ? this.selectedTab.stats.balance_due : "";
    },
  },

  created() {
    addPaymentCompletedEvent(this.onPaymentCompleted);
  },

  beforeDestroy() {
    removePaymentCompletedEvent(this.onPaymentCompleted);
  },

  async mounted() {
    let foundTab;
    const { tab_id } = this.$route.query;

    let tabId = tab_id ? parseInt(tab_id) : null;

    // If we're looking for one tab, grab one tab
    if (!!tabId && NaN != tabId) {
      this.loadTab(tab_id);
    } else {
      this.isLoading = true;
    }

    await Promise.all([
      this.getItems(),
      this.fetchPaymentTypes(),
      this.fetchAllTabs(),
    ]);

    this.items = this.getAvailableItemsUnclassified;
    this.adjustments = this.getAvailableModifiers;

    this.availableItems = this.getAvailableItemsUnclassified;
    this.allTabs = tabNameFormatter(this.getAllTabs, this.getContacts);

    this.isLoading = false;
  },

  methods: {
    ...mapActions([
      "closeTab",
      "getTab",
      "addTabItem",
      "fetchAllTabs",
      "getItems",
      "fetchPaymentTypes",
      "addTabAdjustment",
    ]),

    formatMoney(value) {
      return formatMoney(value, 2, this.getVendor.local_currency);
    },

    setBill() {
      this.$refs.paymentModal.setAllowCardReader(false);
    },

    tableFilter(_data) {
      let doShow = true;

      // Check search string for existence
      let searchString = parseString(_data.name).toLowerCase();
      if (searchString) {
        if (!searchString.includes(this.filterString.toLowerCase())) {
          doShow = false;
        }
      }

      // Deal with filters, the simple way.
      if (true == this.openTabsOnly && !("1" == _data.isOpen)) {
        doShow = false;
      }
      if (true == this.activeTabsOnly && !("1" == _data.isActive)) {
        doShow = false;
      }

      return doShow;
    },

    disableCardReader() {
      this.$refs.paymentModal.setAllowCardReader(false);
    },

    async initiateCharge(paymentData) {
      switch (paymentData.paymentMethodId) {
        case PAYMENT_METHODS.TERMINAL:
          let charged = await this.terminalCharge(paymentData);
          if (charged) {
            await this.addPayment(paymentData, this);
          }
          break;

        case PAYMENT_METHODS.IOS_TERMINAL:
          this.tabsLoading = true;

          dispatchMakePaymentEvent({
            ...paymentData,
            currency: "USD",
          });

          break;

        default:
          this.tabsLoading = true;
          await this.addPayment(paymentData, this);
          this.tabsLoading = false;
          break;
      }

      if (this.tabsLoading) {
        return;
      }

      this.$refs.paymentModal.hide();
    },

    async closeOutTab() {
      this.isLoading = true;

      try {
        await this.closeTab(this.selectedTab.id);
        this.successNotification(`${this.selectedGuest}'s tab closed.`);
        this.selectedTab = null;
        this.clearTabs();
      } catch (e) {
        this.errorNotification(
          `Failed to close ${this.selectedGuest}'s tab: ${e.message}`
        );
      }

      this.isLoading = false;
    },

    async loadTab(tabId) {
      if (tabId != null && tabId.constructor.name === "Object") {
        tabId = tabId.id;
      }

      this.$router.replace({
        query: {
          tab_id: tabId,
        },
      });

      this.isLoading = true;
      this.selectedTab = await this.getTab(tabId);

      this.selectedGuest = `${this.selectedTab.guest.first_name} ${this.selectedTab.guest.last_name}`;

      this.isOpen = !!this.selectedTab.is_open;
      this.isActive = !!this.selectedTab.is_active;

      this.isLoading = false;
    },

    clearTabs() {
      this.selectedGuest = null;
      this.selectedTab = null;
      this.isOpen = null;
      this.isActive = null;
      // this.filter = ""
      this.$router.replace({
        query: {},
      });
    },

    updateCurrentTab(tabItemData) {
      this.updateTab(tabItemData, this);
    },

    async removeItemFromTab(tabItem) {
      if (this.isOpen) {
        const itemInfo = this.items.find(
          (availableItem) => availableItem.id === tabItem.item_id
        );

        try {
          await this.deleteItemFromTab(tabItem, this);
          this.successNotification(`${itemInfo.name} has removed from the tab`);
        } catch (e) {
          this.errorNotification(
            `Failed to remove ${tabItem.name} from the tab`
          );
        }
      } else {
        this.errorNotification(`You cannot alter a closed tab.`);
      }
    },

    async onPaymentCompleted(event) {
      console.log("Tab > Payment Completed event", event.detail);
      let event_data = event.detail;

      this.tabsLoading = false;

      if (event_data) {
        if (event_data.success) {
          await this.addPayment(event_data, this);
        } else {
          this.errorNotification(
            `Payment is failed: ${event_data.reason || "unknown error"}`
          );
        }
      } else {
        this.errorNotification(`Payment is failed: received unknown event`);
      }
    },
  },

  watch: {
    getContacts: {
      handler(newValue, oldValue) {
        this.allTabs = tabNameFormatter(this.allTabs, newValue);
      },
    },
    getAllTabs: {
      handler(allTabs, oldValue) {
        this.allTabs = tabNameFormatter(allTabs, this.getContacts);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  width: 100%;
  align-content: space-between;
  text-align: left;
}

.page-title {
  width: 100%;
  font-size: 1.75rem;
  line-height: 54px;
  font-weight: 500;
  text-transform: uppercase;
  left: 0px;
  height: 54px;
  padding-left: 10px;
  margin: 0px;
  position: absolute;
  display: none;
}

.search-header {
  background: #f7f7f7;
  padding: 5px 0px 0px 0px;
  position: relative;
  width: 100%;
  height: 3rem;
  box-sizing: border-box;
  margin: 0px;
}

th {
  border-top: none;
  outline: none !important;
}

.tabs-label {
  text-align: right;
  width: 30%;
  padding-right: 90px;
}

.contact-modal-btn-new {
  position: absolute;
  right: 12px;
  top: 28px;
}

.row-photo-container {
  background-color: gray;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  height: 64px;
  width: 64px;
  border-radius: 50%;
}

.contacts-table td {
  padding: 6px 20px 6px 20px;
}

.tab-payments-header {
  background: #f7f7f7;
  width: 100%;
  height: 3.25rem;
  padding: 0.25rem;
  table {
    width: 100%;
  }
}

.contacts-table {
  background: #f7f7f7;
}

.tab-actions {
  display: block;
  max-width: 700px;
  margin: 1.5em auto;

  .button-group {
    display: block;
    width: 100%;
    margin-top: 15px;

    button {
      margin: 0 10px;
    }
  }
}

.tab-filter-switch {
  // float: left;
  margin-left: 15px;
  // display: inline-block;

  .tab-filter-label {
    margin-right: 5px;
  }
}

.tab-status-badges {
  margin: 10px;
}

.back-button {
  font-size: 2.5rem;
  right: 1.5rem;
  top: 0.75rem;
  display: inline-block;
  position: absolute;
  cursor: pointer;
}

.locked-tab {
  i.fi-x-circle {
    cursor: not-allowed;
  }
}

.invoice-display-container {
  background: rgba(0, 0, 0, 0.8);
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  text-align: center;
}

.tab-table {
  overflow-y: auto;
  position: relative;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 106px);
  background: white;
}

.tab-page-container {
  overflow: hidden;
  height: 100%;
  width: 100%;
}

.invoice-control-button {
  width: 9rem;
  margin: 0.25rem;
  // height: 4rem;
}

.guest-card {
  font-size: 24pt;
  text-align: left;
  padding-top: 1rem;
  padding-left: 5.4rem;
  margin-bottom: -1rem;
}

.tabs-loading {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
}
</style>

<style lang="scss">
.invoice-display-box {
  max-width: 55rem;
  overflow: auto;
}
</style>
